import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Percent, Plus, RefreshCw, Loader2, Infinity as Infty, IndianRupee, Calendar as CalendarIcon, MapPin,
} from "lucide-react";

/**
 * Franchise-facing Billing Coupons management.
 *
 * A Franchise operator creates coupons that are AUTOMATICALLY scoped
 * to their own assigned_city — the server forces this regardless of
 * what the client sends, so a franchise cannot accidentally (or
 * maliciously) create a platform-wide coupon. Only Super Admin can
 * do that (via the /superadmin/billing-coupons endpoint).
 *
 * Same "one code, four surfaces" contract as the Super Admin panel:
 * a franchise-created MUM50 coupon works on campaign fee, wallet
 * top-up, listing duration, AND featured-slot Boost — but ONLY when
 * the checkout's own city matches Mumbai.
 */
export default function FranchiseBillingCouponsPage() {
  const { admin } = useAdminAuth();
  const city = admin?.assigned_city || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percentage", discount_value: "20",
    max_uses: "", note: "", expires_at: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.franchiseListBillingCoupons();
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load coupons.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (busy) return;
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      note: form.note.trim() || null,
      ...(form.expires_at ? { expires_at: `${form.expires_at}T00:00:00Z` } : {}),
    };
    if (!payload.code || payload.code.length < 2) { toast.error("Code must be at least 2 characters."); return; }
    if (!(payload.discount_value > 0)) { toast.error("Discount value must be greater than 0."); return; }
    if (payload.discount_type === "percentage" && payload.discount_value > 100) {
      toast.error("Percentage can't exceed 100."); return;
    }
    setBusy(true);
    try {
      await api.franchiseCreateBillingCoupon(payload);
      toast.success(`Created ${payload.code} for ${city}`);
      setForm({ code: "", discount_type: "percentage", discount_value: "20", max_uses: "", note: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create coupon.");
    } finally {
      setBusy(false);
    }
  };

  if (!city) {
    return (
      <div className="p-6">
        <Card className="p-6">No city assigned to your account.</Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="franchise-billing-coupons-page">
      <Toaster richColors position="top-center" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Percent className="h-6 w-6" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Billing coupons — {city}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Discounts you create here work on campaign fees, wallet top-ups, listing durations, and featured-slot Boosts — but only for checkouts in <b>{city}</b>. Platform-wide coupons are Super Admin only.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fr-coupons-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="p-5 md:p-6">
        <h2 className="font-bold mb-4 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Create city-scoped coupon
        </h2>
        <form onSubmit={onCreate} className="space-y-4">
          <div className="rounded-lg bg-secondary/40 px-3 py-2 text-xs flex items-center gap-2" data-testid="fr-coupon-city-badge">
            <MapPin className="h-3.5 w-3.5" />
            City scope <Badge variant="secondary" className="ml-1 uppercase tracking-wider">{city}</Badge>
            <span className="text-muted-foreground">— locked, cannot be changed.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MONSOON20" className="uppercase tracking-wider" data-testid="fr-coupon-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Internal note" data-testid="fr-coupon-note" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Discount type</Label>
              <RadioGroup value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })} className="flex gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="percentage" id="fdt-pct" data-testid="fr-coupon-type-pct" />
                  <Label htmlFor="fdt-pct" className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> Percentage</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="flat" id="fdt-flat" data-testid="fr-coupon-type-flat" />
                  <Label htmlFor="fdt-flat" className="flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Flat</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label>{form.discount_type === "percentage" ? "Percentage (%)" : "Flat amount (₹)"}</Label>
              <Input type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="fr-coupon-value" />
            </div>
            <div className="space-y-1.5">
              <Label>Max uses <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
              <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" data-testid="fr-coupon-maxuses" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5" /> Expiry date <span className="text-muted-foreground text-xs">(blank = no expiry)</span>
              </Label>
              <Input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="fr-coupon-expires-at"
              />
              <p className="text-[11px] text-muted-foreground">
                Valid through 23:59:59 IST on the selected date.
              </p>
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="fr-coupon-create">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : <><Plus className="h-4 w-4 mr-1.5" /> Create coupon</>}
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="fr-coupons-empty">
            No coupons yet. Create the first one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fr-coupons-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Discount</th>
                  <th className="py-3 px-4">Usage</th>
                  <th className="py-3 px-4">Expires</th>
                  <th className="py-3 px-4">City</th>
                  <th className="py-3 px-4">Note</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`fr-coupon-row-${r.id}`}>
                    <td className="py-3 px-4 font-mono font-semibold">{r.code}</td>
                    <td className="py-3 px-4">
                      {r.discount_type === "percentage" ? `${r.discount_value}%` : `₹${Number(r.discount_value).toFixed(2)}`}
                    </td>
                    <td className="py-3 px-4 tabular-nums">
                      {r.max_uses == null
                        ? <span className="inline-flex items-center gap-1">{r.uses_count} <Infty className="h-3 w-3 opacity-60" /></span>
                        : <>{r.uses_count} / {r.max_uses}</>}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      {r.expires_at
                        ? new Date(r.expires_at).toLocaleDateString()
                        : <span className="text-muted-foreground italic">No expiry</span>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-xs">{r.city_scope || "Platform-wide"}</Badge>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground max-w-[220px] truncate">{r.note || "—"}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={r.is_active ? "bg-emerald-100 text-emerald-900 border-emerald-300" : ""}>
                        {r.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
